import z from "zod";
import { noticeTotalMinutes } from "@/lib/utils/notice-format";
import {
  addVariantColumnIssues,
  variantColumnsTuple,
} from "./appointment-variant-column-schema";

const dateValueSchema = z.object({
  day: z.string(),
  month: z.string(),
  year: z.string(),
});

const intPreprocess = (val: unknown): number | undefined => {
  if (typeof val === "number") return val;
  if (typeof val === "string") {
    if (val === "") return undefined;
    const parsed = parseInt(val, 10);
    return isNaN(parsed) ? undefined : parsed;
  }
  if (val === null || val === undefined) return undefined;
  return undefined;
};

const noticeField = (label: string) =>
  z.preprocess(
    (val) => {
      if (val === "" || val === null || val === undefined) return 0;
      if (typeof val === "string") {
        const parsed = parseInt(val, 10);
        return isNaN(parsed) ? 0 : parsed;
      }
      return typeof val === "number" ? val : 0;
    },
    z.number().min(0, { message: `${label} must be 0 or greater` }),
  );

export const appointmentEditFormSchema = z
  .object({
    appointmentId: z.preprocess(
      intPreprocess,
      z.number().min(1, { message: "Appointment ID is required" }),
    ),
    name: z.string().min(1, { message: "Name is required" }),
    description: z.string().nullable().optional(),
    columns: variantColumnsTuple,
    allowOnlineBooking: z.boolean(),
    allowCancellation: z.boolean(),
    bookingNoticeDays: noticeField("Days"),
    bookingNoticeHours: noticeField("Hours"),
    bookingNoticeMinutes: noticeField("Minutes"),
    cancellationNoticeDays: noticeField("Days"),
    cancellationNoticeHours: noticeField("Hours"),
    cancellationNoticeMinutes: noticeField("Minutes"),
    startDate: z
      .union([dateValueSchema, z.null()])
      .refine(
        (val) => val !== null && Boolean(val.day && val.month && val.year),
        { message: "Start date is required" },
      ),
    endDate: dateValueSchema.nullable().optional(),
    teacherId: z.preprocess(
      intPreprocess,
      z
        .union([z.number(), z.undefined()])
        .refine((val) => val !== undefined, { message: "Teacher is required" })
        .pipe(z.number().min(1, { message: "Teacher is required" })),
    ),
    requirePayment: z.boolean(),
    isApprovalRequired: z.boolean(),
    isActive: z.boolean(),
    classRooms: z.array(
      z.number().min(1, { message: "Classroom ID must be at least 1" }),
    ),
  })
  .refine(
    (data) => {
      if (
        data.startDate &&
        data.endDate &&
        data.startDate.year &&
        data.startDate.month &&
        data.startDate.day &&
        data.endDate.year &&
        data.endDate.month &&
        data.endDate.day
      ) {
        const startDateStr = `${data.startDate.year}-${data.startDate.month.padStart(2, "0")}-${data.startDate.day.padStart(2, "0")}`;
        const endDateStr = `${data.endDate.year}-${data.endDate.month.padStart(2, "0")}-${data.endDate.day.padStart(2, "0")}`;
        return endDateStr >= startDateStr;
      }
      return true;
    },
    {
      message: "End date must be after or equal to start date",
      path: ["endDate"],
    },
  )
  .refine(
    (data) =>
      noticeTotalMinutes(
        data.bookingNoticeDays,
        data.bookingNoticeHours,
        data.bookingNoticeMinutes,
      ) > 0,
    {
      message:
        "Booking notice is required (set at least one of days, hours, or minutes)",
      path: ["bookingNoticeDays"],
    },
  )
  .refine(
    (data) =>
      data.allowCancellation !== true ||
      noticeTotalMinutes(
        data.cancellationNoticeDays,
        data.cancellationNoticeHours,
        data.cancellationNoticeMinutes,
      ) > 0,
    {
      message:
        "Cancellation notice is required (set at least one of days, hours, or minutes)",
      path: ["cancellationNoticeDays"],
    },
  )
  .superRefine((data, ctx) => {
    addVariantColumnIssues(data.columns, ctx);
  });

export type AppointmentEditFormSchemaType = z.infer<
  typeof appointmentEditFormSchema
>;
